import {inject, Injectable} from './di';
import {AppInitializeService} from './app-initialize-service';
import {distinctUntilChanged, fromEvent, map, tap} from 'rxjs';
import {Logger} from './logger';
import {StorageService} from './storage-service';
import {RoomNumberService} from './room-number-service';

@Injectable()
export class NavigationService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly storageService = inject(StorageService);
  private readonly roomNumberService = inject(RoomNumberService);

  /**
   * Current URL subject
   */
  public readonly currentUrl$ = fromEvent(window, 'popstate').pipe(
    map(() => this.getCurrentUrl()),
    distinctUntilChanged(),
    tap((url) => {
      this.logger.debug(`[NavigationService] URL changed to`, url);
    }),
  );

  constructor() {
    this.roomNumberService.roomNumber$.pipe(distinctUntilChanged()).subscribe((room) => {
      if (room) {
        this.setHashParam('room', room);
      } else {
        this.removeHashParam('room');
      }
    });

    window.hash = {
      get: (name: string) => this.getHashParam(name),
      set: (name: string, value: string | null) => {
        if (value == null) {
          this.removeHashParam(name);
        } else {
          this.setHashParam(name, value);
        }
      },
    };
  }

  public async init(): Promise<boolean> {
    try {
      // Fron legacy code:
      // HotezaHub
      const hhurl = this.getHashParam('hhurl');
      if (hhurl) {
        this.storageService.set('hhurl', hhurl);
        this.removeHashParam('hhurl');
      } else {
        // TODO: убрано до момента решения удаления урла при перезагрузке страницы
        // this.storageService.remove('hhurl');
      }
    } catch (err) {
      this.logger.error('[NavigationService] Unable to initialize', err);
    }

    return Promise.resolve(true);
  }

  /**
   * Hash Parameters Management
   */

  /**
   * Get all hash parameters as a key-value map
   * @returns Object containing all hash parameters
   */
  public getHashParams(): Record<string, string> {
    return (window.location.hash || '#')
      .substring(1) // Remove the '#'
      .split('&')
      .filter((e) => !!e)
      .reduce(
        (params, pair) => {
          const [key, value] = pair.split('=');
          if (key) {
            params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
          }
          return params;
        },
        {} as Record<string, string>,
      );
  }

  /**
   * Set all hash parameters from a key-value map
   * @param params Object containing hash parameters to set
   */
  setHashParams(params: Record<string, string>): void {
    this.logger.debug('[NavigationService] Setting URL hash params', params);
    this.applyHashParams(params);
  }

  /**
   * Get a specific hash parameter by name
   * @param name Parameter name
   * @returns Parameter value or null if not found
   */
  getHashParam(name: string): string | null {
    const params = this.getHashParams();
    return params[name] || null;
  }

  /**
   * Set a specific hash parameter
   * @param name Parameter name
   * @param value Parameter value
   */
  setHashParam(name: string, value: string): void {
    const params = this.getHashParams();
    params[name] = value;
    this.logger.debug(`[NavigationService] Settings URL hash param "${name}": "${value}"`);
    this.applyHashParams(params);
  }

  /**
   * Remove a specific hash parameter
   * @param name Parameter name to remove
   */
  removeHashParam(name: string): void {
    this.logger.debug(`[NavigationService] Removing URL hash param ${name}`);
    const params = this.getHashParams();
    delete params[name];
    this.applyHashParams(params);
  }

  /**
   * Clear all hash parameters
   */
  clearHash(): void {
    this.logger.debug(`[NavigationService] Clearing URL hash params`);
    window.location.hash = '';
  }

  private applyHashParams(params: Record<string, string>): void {
    window.location.hash = Object.entries(params)
      .filter(([key, value]) => key && value !== undefined)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
  }

  /**
   * Query Parameters Management
   */

  /**
   * Get all query parameters as a key-value map
   * @returns Object containing all query parameters
   */
  getQueryParams(): Record<string, string> {
    const search = window.location.search;
    if (!search || search === '?') {
      return {};
    }

    const queryString = search.substring(1); // Remove the '?'
    const params: Record<string, string> = {};

    const pairs = queryString.split('&');
    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key) {
        params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
      }
    }

    return params;
  }

  /**
   * Set all query parameters from a key-value map
   * @param params Object containing query parameters to set
   * @param replaceState Whether to replace current history state (default: true)
   */
  setQueryParams(params: Record<string, string>, replaceState: boolean = true): void {
    this.logger.debug(`[NavigationService] Setting URL query params`, params);
    this.applyQueryParams(params, replaceState);
  }

  /**
   * Get a specific query parameter by name
   * @param name Parameter name
   * @returns Parameter value or null if not found
   */
  getQueryParam(name: string): string | null {
    const params = this.getQueryParams();
    return params[name] || null;
  }

  /**
   * Set a specific query parameter
   * @param name Parameter name
   * @param value Parameter value
   * @param replaceState Whether to replace current history state (default: true)
   */
  setQueryParam(name: string, value: string, replaceState: boolean = true): void {
    const params = this.getQueryParams();
    params[name] = value;
    this.logger.debug(
      `[NavigationService] Setting URL query param "${name}": "${value}" ${replaceState ? ', replacing current history state' : ''}`,
    );
    this.applyQueryParams(params, replaceState);
  }

  /**
   * Remove a specific query parameter
   * @param name Parameter name to remove
   * @param replaceState Whether to replace current history state (default: true)
   */
  removeQueryParam(name: string, replaceState: boolean = true): void {
    this.logger.debug(`[NavigationService] Removing URL query param ${name}`);
    const params = this.getQueryParams();
    delete params[name];
    this.applyQueryParams(params, replaceState);
  }

  /**
   * Clear all query parameters
   * @param replaceState Whether to replace current history state (default: true)
   */
  clearQueryParams(replaceState: boolean = true): void {
    this.logger.debug(`[NavigationService] Clearing URL query params`);

    const newUrl = this.buildUrl(window.location.pathname, '', window.location.hash);

    if (replaceState) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }

  private applyQueryParams(params: Record<string, string>, replaceState: boolean = true): void {
    const queryString = Object.entries(params)
      .filter(([key, value]) => key && value !== undefined)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    const newUrl = this.buildUrl(window.location.pathname, queryString, window.location.hash);

    if (replaceState) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }

  /**
   * Navigation and History Management
   */

  /**
   * Navigate to a specific URL
   * @param url URL to navigate to
   * @param replaceState Whether to replace current history state (default: false)
   */
  navigateTo(url: string, replaceState: boolean = false): void {
    this.logger.debug(`[NavigationService] Navigating to `, url);

    if (replaceState) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
  }

  /**
   * Go back in history
   * @param steps Number of steps to go back (default: 1)
   */
  goBack(steps: number = 1): void {
    this.logger.debug(`[NavigationService] Going back in history for ${steps} steps`);
    window.history.go(-Math.abs(steps));
  }

  /**
   * Go forward in history
   * @param steps Number of steps to go forward (default: 1)
   */
  goForward(steps: number = 1): void {
    this.logger.debug(`[NavigationService] Going forward in history for ${steps} steps`);
    window.history.go(Math.abs(steps));
  }

  /**
   * Reload the current page
   * @param forceReload Whether to force reload from server (default: false). Only supported in Firefox.
   */
  reload(forceReload: boolean = false): void {
    this.logger.debug(`[NavigationService] Reloading the app`);

    // @ts-expect-error Expected 0 arguments, but got 1
    window.location.reload(forceReload);
  }

  /**
   * Replace the current URL without adding to history
   * @param url New URL
   */
  replace(url: string): void {
    this.logger.debug(`[NavigationService] Navigating to "${url}" without adding to history`);

    window.location.replace(url);
  }

  /**
   * Get the current full URL
   * @returns Current URL as string
   */
  getCurrentUrl(): string {
    return window.location.href;
  }

  /**
   * Get the current pathname
   * @returns Current pathname
   */
  getCurrentPath(): string {
    return window.location.pathname;
  }

  /**
   * Get the current origin (protocol + host)
   * @returns Current origin
   */
  getCurrentOrigin(): string {
    return window.location.origin;
  }

  /**
   * Check if browser supports history API
   * @returns True if history API is supported
   */
  isHistoryApiSupported(): boolean {
    return !!(window.history && window.history.pushState);
  }

  /**
   * Utility Methods
   */

  /**
   * Build a URL from components
   * @param pathname Path component
   * @param search Query string (without ?)
   * @param hash Hash string (with or without #)
   * @returns Complete URL string
   */
  private buildUrl(pathname: string, search: string, hash: string): string {
    let url = pathname;

    if (search) {
      url += '?' + search;
    }

    if (hash) {
      url += hash.startsWith('#') ? hash : '#' + hash;
    }

    return url;
  }

  /**
   * Parse a URL string into its components
   * @param urlString URL to parse
   * @returns Parsed URL components or null if invalid
   */
  parseUrl(urlString: string): {
    protocol: string;
    hostname: string;
    port: string;
    pathname: string;
    search: string;
    hash: string;
    origin: string;
  } | null {
    try {
      // Create a temporary anchor element to parse the URL
      const anchor = document.createElement('a');
      anchor.href = urlString;

      return {
        protocol: anchor.protocol,
        hostname: anchor.hostname,
        port: anchor.port,
        pathname: anchor.pathname,
        search: anchor.search,
        hash: anchor.hash,
        origin: anchor.protocol + '//' + anchor.hostname + (anchor.port ? ':' + anchor.port : ''),
      };
    } catch {
      return null;
    }
  }

  /**
   * Check if a URL is external (different origin)
   * @param url URL to check
   * @returns True if URL is external
   */
  isExternalUrl(url: string): boolean {
    const parsed = this.parseUrl(url);
    if (!parsed) {
      return false;
    }

    const currentOrigin =
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '');

    return parsed.origin !== currentOrigin;
  }

  /**
   * Build a URL with query parameters
   * @param baseUrl Base URL
   * @param params Query parameters to add
   * @returns Complete URL with parameters
   */
  buildUrlWithParams(baseUrl: string, params: Record<string, string>): string {
    const parsed = this.parseUrl(baseUrl);
    if (!parsed) {
      return baseUrl;
    }

    const queryString = Object.entries(params)
      .filter(([key, value]) => key && value !== undefined)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    return this.buildUrl(parsed.pathname, queryString, parsed.hash);
  }
}
